import type { UserResponse } from '@descope/core-js-sdk';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Re-export UserResponse with a semantic alias
export type DescopeUser = UserResponse;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extract display name from Descope user object for personalization.
 * Does NOT fall back to email to avoid PII leakage and awkward
 * personalization (e.g., "Hi john@example.com").
 *
 * Tries:
 * 1. name property
 * 2. givenName + middleName + familyName
 * 3. undefined if no actual name available
 */
export function getDisplayName(
  user: DescopeUser | null | undefined
): string | undefined {
  if (!user) {
    return undefined;
  }

  // 1. Try name property first
  if (user.name && user.name.trim()) {
    return user.name.trim();
  }

  // 2. Try givenName + middleName + familyName
  const givenName = user.givenName?.trim() || '';
  const middleName = user.middleName?.trim() || '';
  const familyName = user.familyName?.trim() || '';
  if (givenName || middleName || familyName) {
    return [givenName, middleName, familyName].filter(Boolean).join(' ').trim();
  }

  // 3. No display name available (do NOT fall back to email)
  return undefined;
}

/**
 * Extract username from Descope user object.
 * Tries display name first, then falls back to email.
 *
 * Tries:
 * 1. Display name (name property or givenName + middleName + familyName)
 * 2. email address
 * 3. undefined if none available
 */
export function getUsername(
  user: DescopeUser | null | undefined
): string | undefined {
  const displayName = getDisplayName(user);
  if (displayName) {
    return displayName;
  }

  // Fallback to email
  return user?.email?.trim() || undefined;
}

/**
 * Format API user (e.g. from @kirin/api-client contracts) for display.
 * Returns name, or email, or id so something is always shown.
 * Use for tables, tooltips, and anywhere we show a user from API responses.
 */
export function getUserDisplayName(user: {
  id: string;
  name?: string | null;
  email?: string | null;
  userType?: string | null;
}): string {
  // KIR-3908: the Unattributed placeholder has no name/email; never fall back
  // to its raw id (an internal UUID) — show a clear label instead.
  if (user.userType === 'unattributed') return 'Unattributed';
  return user.name || user.email || user.id;
}

/**
 * Decode a base64url-encoded string to a UTF-8 string.
 *
 * Browsers don't provide a native base64url decoder, so we convert
 * to standard base64 (replace URL-safe chars, add padding) and use atob().
 *
 * @param input - base64url-encoded string (RFC 4648 §5)
 * @returns decoded UTF-8 string
 */
export function decodeBase64Url(input: string): string {
  // Replace URL-safe characters with standard base64 equivalents
  let base64 = input.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding (base64 requires length to be a multiple of 4)
  const paddingNeeded = (4 - (base64.length % 4)) % 4;
  base64 += '='.repeat(paddingNeeded);

  return atob(base64);
}

/**
 * Extract filename from a file path
 * Returns the last segment of a path after splitting by '/' or '\'
 * Handles special cases like vscode:// protocol, absolute paths, relative paths, and Windows paths
 * @param filePath - The file path to extract the filename from
 * @returns The filename, or '—' if the path is null, undefined, empty, or ends with a trailing slash
 */
export function getFileName(filePath: string | null | undefined): string {
  if (!filePath) return '—';

  // Handle Windows paths (backslashes) and Unix paths (forward slashes)
  const parts = filePath.split(/[/\\]/);
  const lastPart = parts[parts.length - 1];

  // If the last part is empty (path ends with slash), return '—' for consistency
  if (lastPart === '') {
    return '—';
  }

  return lastPart || filePath;
}
